// flow/hooks/useFlowGlobalHandlers.ts
import { useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import type { Node } from '@xyflow/react'

interface UseFlowGlobalHandlersProps {
    nodes: Node[]
    user: any
    screenToFlowPosition: (position: { x: number; y: number }) => { x: number; y: number }
    getNode: (id: string) => Node | undefined
    updateNodeData: (nodeId: string, data: any) => void
    addNode: (node: Node) => void
    handleSectionSelect: (nodeId: string, sectionId: string, sectionName: string, eventData: any) => void
    deleteNodeWithGroups: (nodeId: string) => void
    duplicateNode: (nodeId: string) => void
}

export function useFlowGlobalHandlers({
    nodes,
    user,
    screenToFlowPosition,
    getNode,
    updateNodeData,
    addNode,
    handleSectionSelect,
    deleteNodeWithGroups,
    duplicateNode,
}: UseFlowGlobalHandlersProps) {

    // Message display function
    const showMessage = useCallback((detail: string, summary?: string, severity?: string) => {
        switch (severity) {
            case 'success':
                toast.success(summary || 'Success', { description: detail })
                break
            case 'error':
                toast.error(summary || 'Error', { description: detail })
                break
            case 'warn':
                toast.warning(summary || 'Warning', { description: detail })
                break
            default:
                toast.info(summary || 'Information', { description: detail })
                break
        }
    }, [])

    // Set up global window functions
    useEffect(() => {
        ; (window as any).__flowGetAllNodes = () => nodes
            ; (window as any).__flowSectionSelect = (
                nodeId: string,
                sectionId: string,
                sectionName: string,
                eventData: any
            ) => {
                handleSectionSelect(nodeId, sectionId, sectionName, eventData)
            }
            ; (window as any).__flowDeleteNode = deleteNodeWithGroups
            ; (window as any).__flowDuplicateNode = duplicateNode
            ; (window as any).__flowUpdateNodeData = (nodeId: string, data: any) => {
                updateNodeData(nodeId, (prevData: any) => ({ ...prevData, ...data }))
            }

        return () => {
            delete (window as any).__flowGetAllNodes
            delete (window as any).__flowSectionSelect
            delete (window as any).__flowDeleteNode
            delete (window as any).__flowDuplicateNode
            delete (window as any).__flowUpdateNodeData
        }
    }, [handleSectionSelect, deleteNodeWithGroups, duplicateNode, nodes, updateNodeData])

    // Set up custom event listeners
    useEffect(() => {
        const handleCreateKnowledgeExtractionNode = (
            event: CustomEventInit<{
                fileId: string
                filename: string
                nodeId: string
            }>
        ) => {
            const position = screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            })
            const newNode: Node = {
                id: event.detail!.nodeId,
                type: 'nodeknowledgeextraction',
                position,
                data: {
                    label: 'Knowledge Extraction',
                    informationClassification: 'Data',
                    fileId: event.detail!.fileId,
                    filename: event.detail!.filename,
                },
            }
            addNode(newNode)
        }

        const handleCreateDocumentTemplateNode = (
            event: CustomEventInit<{
                fileId: string
                filename: string
                nodeId: string
            }>
        ) => {
            const position = screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            })
            const newNode: Node = {
                id: event.detail!.nodeId,
                type: 'nodedocumenttemplate',
                position,
                data: {
                    label: 'Document Template',
                    informationClassification: 'Information',
                    fileId: event.detail!.fileId,
                    filename: event.detail!.filename,
                },
            }
            addNode(newNode)
        }

        const handleCreateFileMinerNode = (
            event: CustomEventInit<{
                fileId: string
                filename: string
                nodeId: string
            }>
        ) => {
            const position = screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            })
            const newNode: Node = {
                id: event.detail!.nodeId,
                type: 'nodefileminer',
                position,
                data: {
                    label: 'File Miner',
                    informationClassification: 'Data',
                    blobIds: [event.detail!.fileId],
                    userPrompt: 'Analyze this file',
                    createdBy: user.email,
                },
            }
            addNode(newNode)
        }

        const handleGetSectionContent = (
            event: CustomEventInit<{
                nodeId: string
                sectionId: string
                callback: (content: string) => void
            }>
        ) => {
            // Get the node containing the section
            const sourceNode = getNode(event.detail!.nodeId)
            if (!sourceNode) return

            // Extract section content from the node data
            let sectionContent = ''

            // Check if it's a document node with markdown content
            if (
                sourceNode.data?.contentMarkdown &&
                typeof sourceNode.data.contentMarkdown === 'string'
            ) {
                const markdown = sourceNode.data.contentMarkdown
                // Parse sections by ::: delimiters or # headers
                const sections = markdown.split(/(?=^#{1,6}\s|^:::)/m)

                // Find the section by ID (assuming section ID relates to position or header)
                const sectionIndex =
                    parseInt(event.detail!.sectionId.replace(/\D/g, '')) || 0
                sectionContent = sections[sectionIndex] || markdown // Fallback to full content
            } else if (
                sourceNode.data?.content &&
                typeof sourceNode.data.content === 'string'
            ) {
                sectionContent = sourceNode.data.content
            } else {
                sectionContent = `Content from ${sourceNode.data?.label || 'Untitled Section'}`
            }

            // Execute the callback with the section content
            event.detail!.callback(sectionContent)
        }

        const handleCreateResourceNode = (
            event: CustomEventInit<{
                fileId: string
                filename: string
                url: string
                contentType: string
                size: number
                createdAt: string
                groupId: string | null
            }>
        ) => {
            const position = screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            })
            const newNode: Node = {
                id: `resource-node-${Date.now()}`,
                type: 'NodeResource',
                position,
                width: 464,
                height: 692,
                data: {
                    label: 'File Resource',
                    informationClassification: 'Data',
                    filename: event.detail!.filename,
                    createdBy: user.email,
                    createdAt: event.detail!.createdAt,
                    url: event.detail!.url,
                    fileId: event.detail!.fileId,
                    contentType: event.detail!.contentType,
                    size: event.detail!.size,
                    groupId: event.detail!.groupId,
                },
            }
            addNode(newNode)
        }

        const handleCreateResourcePDFNode = (
            event: CustomEventInit<{
                fileId: string
                filename: string
                url: string
                contentType: string
                size: number
                createdAt: string
                groupId: string | null
            }>
        ) => {
            const position = screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            })
            const newNode: Node = {
                id: `pdf-resource-node-${Date.now()}`,
                type: 'NodeResourcePDF',
                position,
                width: 464,
                height: 692,
                data: {
                    label: 'PDF Resource',
                    informationClassification: 'Data',
                    filename: event.detail!.filename,
                    createdBy: user.email,
                    createdAt: event.detail!.createdAt,
                    url: event.detail!.url,
                    fileId: event.detail!.fileId,
                    contentType: event.detail!.contentType,
                    size: event.detail!.size,
                    groupId: event.detail!.groupId,
                },
            }
            addNode(newNode)
        }

        // Add event listeners
        document.addEventListener('create-resource-pdf-node', handleCreateResourcePDFNode as any)
        document.addEventListener('create-resource-node', handleCreateResourceNode as any)
        document.addEventListener('create-knowledge-extraction-node', handleCreateKnowledgeExtractionNode as any)
        document.addEventListener('create-document-template-node', handleCreateDocumentTemplateNode as any)
        document.addEventListener('create-file-miner-node', handleCreateFileMinerNode as any)
        document.addEventListener('getSectionContent', handleGetSectionContent as any)

        return () => {
            document.removeEventListener('create-resource-pdf-node', handleCreateResourcePDFNode as any)
            document.removeEventListener('create-resource-node', handleCreateResourceNode as any)
            document.removeEventListener('create-knowledge-extraction-node', handleCreateKnowledgeExtractionNode as any)
            document.removeEventListener('create-document-template-node', handleCreateDocumentTemplateNode as any)
            document.removeEventListener('create-file-miner-node', handleCreateFileMinerNode as any)
            document.removeEventListener('getSectionContent', handleGetSectionContent as any)
        }
    }, [screenToFlowPosition, addNode, user.email, getNode])

    return {
        showMessage,
    }
}
